import React from 'react';
import { Link } from 'react-router-dom';
import {
  Star,
  CheckSquare,
  Clock,
  Calendar,
  Bell,
  Users,
  ChevronRight,
  Shield,
  Sparkles,
  BarChart3,
} from 'lucide-react';

const LandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <span className="text-2xl">📋</span>
              <span className="text-xl font-bold text-gray-900">LEES</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-gray-600 hover:text-gray-900 transition">Features</a>
              <a href="#how-it-works" className="text-sm text-gray-600 hover:text-gray-900 transition">How It Works</a>
              <a href="#pricing" className="text-sm text-gray-600 hover:text-gray-900 transition">Pricing</a>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/checklist"
                className="text-sm font-medium text-indigo-600 hover:text-indigo-700 transition"
              >
                Log in
              </Link>
              <a
                href="#get-started"
                className="text-sm font-medium px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition shadow-sm"
              >
                Get Started
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply opacity-30 animate-pulse" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-200 rounded-full mix-blend-multiply opacity-30 animate-pulse" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-200 rounded-full mix-blend-multiply opacity-20 animate-pulse" style={{ animationDelay: '4s' }} />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Built for busy families
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tight leading-tight">
              Daily schedules your kids{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                actually follow
              </span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
              LEES helps parents manage multiple children's daily routines with visual timelines,
              interactive checklists, and a star reward system that keeps kids motivated.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#get-started"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-200"
              >
                Start Free Trial
                <ChevronRight className="w-5 h-5" />
              </a>
              <Link
                to="/timeline"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition border border-gray-200 shadow-sm"
              >
                See Demo
              </Link>
            </div>
            <p className="mt-4 text-sm text-gray-500">
              No credit card required. Free for up to 2 children.
            </p>
          </div>

          {/* App Preview */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="rounded-2xl shadow-2xl overflow-hidden border border-gray-200 bg-gradient-to-br from-blue-50 to-purple-50 p-4 sm:p-8">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {[
                  { name: 'Emma', avatar: '👧🏻', color: 'bg-pink-100 border-pink-300', stars: 7 },
                  { name: 'Erica', avatar: '👧🏻', color: 'bg-purple-100 border-purple-300', stars: 5 },
                  { name: 'Elly', avatar: '👧🏻', color: 'bg-blue-100 border-blue-300', stars: 8 },
                  { name: 'Emilia', avatar: '👧🏻', color: 'bg-green-100 border-green-300', stars: 6 },
                ].map((kid) => (
                  <div
                    key={kid.name}
                    className={`${kid.color} border-2 rounded-xl p-3 sm:p-4 text-center`}
                  >
                    <div className="text-3xl sm:text-4xl mb-2">{kid.avatar}</div>
                    <div className="font-bold text-gray-800 text-sm sm:text-base">{kid.name}</div>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-400" />
                      <span className="text-sm font-semibold text-yellow-700">{kid.stars}</span>
                    </div>
                    <div className="mt-2 space-y-1">
                      {['Brush teeth', 'Breakfast', 'Homework'].map((task, i) => (
                        <div key={task} className="flex items-center gap-1.5 text-xs text-gray-600">
                          <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${i < 2 ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}>
                            {i < 2 && (
                              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span className={i < 2 ? 'line-through text-gray-400' : ''}>{task}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '500+', label: 'Families' },
              { value: '4.8/5', label: 'Parent Rating' },
              { value: '10K+', label: 'Tasks Completed Daily' },
              { value: '92%', label: 'Routine Adherence' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Everything you need to run a smooth household
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              From morning routines to bedtime, LEES keeps every child on track with tools parents actually enjoy using.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Clock className="w-6 h-6" />,
                title: 'Visual Timelines',
                description:
                  'See each child\'s entire day at a glance. Color-coded blocks show activities, meals, classes, and free time with automatic conflict resolution.',
                color: 'bg-blue-100 text-blue-600',
              },
              {
                icon: <CheckSquare className="w-6 h-6" />,
                title: 'Interactive Checklists',
                description:
                  'Kids tap to check off tasks as they go. Satisfying completion feedback keeps them motivated to finish their daily routines independently.',
                color: 'bg-green-100 text-green-600',
              },
              {
                icon: <Star className="w-6 h-6" />,
                title: 'Star Rewards',
                description:
                  'Every completed task earns a star. Kids see their daily star count grow, turning mundane chores into an engaging achievement system.',
                color: 'bg-yellow-100 text-yellow-600',
              },
              {
                icon: <Calendar className="w-6 h-6" />,
                title: 'Weekday & Weekend Schedules',
                description:
                  'Set different routines for school days and weekends. Day-specific activities like ballet or gymnastics are automatically managed.',
                color: 'bg-purple-100 text-purple-600',
              },
              {
                icon: <Bell className="w-6 h-6" />,
                title: 'Slack Notifications',
                description:
                  'Get instant Slack messages when tasks are completed. Stay informed about your children\'s progress without hovering over them.',
                color: 'bg-red-100 text-red-600',
              },
              {
                icon: <Users className="w-6 h-6" />,
                title: 'Multi-Child Support',
                description:
                  'Manage up to four children with individual schedules, personalized task lists, and separate progress tracking all in one view.',
                color: 'bg-indigo-100 text-indigo-600',
              },
            ].map((feature) => (
              <div
                key={feature.title}
                className="group p-6 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300"
              >
                <div className={`inline-flex p-3 rounded-xl ${feature.color} mb-4`}>
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 sm:py-28 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Up and running in minutes
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              No complicated setup. Just add your children, customize their schedules, and let LEES handle the rest.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {[
              {
                step: '1',
                title: 'Add Your Children',
                description:
                  'Set up profiles for each child with their name and avatar. LEES supports up to four children per family.',
                icon: <Users className="w-8 h-8 text-indigo-600" />,
              },
              {
                step: '2',
                title: 'Build Their Schedules',
                description:
                  'Create daily routines with tasks, activities, and time blocks. Set different schedules for weekdays and weekends.',
                icon: <Calendar className="w-8 h-8 text-indigo-600" />,
              },
              {
                step: '3',
                title: 'Track & Celebrate',
                description:
                  'Kids check off tasks throughout the day and earn stars. Parents stay informed with real-time notifications.',
                icon: <BarChart3 className="w-8 h-8 text-indigo-600" />,
              },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg border border-gray-100 mb-6">
                  {item.icon}
                </div>
                <div className="inline-flex items-center justify-center w-8 h-8 bg-indigo-600 text-white text-sm font-bold rounded-full mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Loved by families
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              See what parents are saying about managing their household with LEES.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                quote:
                  'My mornings used to be chaos with three kids. Now they check their LEES timeline and get ready on their own. It\'s been a game changer.',
                name: 'Sarah M.',
                role: 'Mom of 3',
              },
              {
                quote:
                  'The star system actually works. My kids compete to see who can earn the most stars each day. Homework gets done without nagging!',
                name: 'David L.',
                role: 'Dad of 2',
              },
              {
                quote:
                  'I love getting the Slack notifications when my kids finish their tasks at home while I\'m at work. Gives me peace of mind.',
                name: 'Priya K.',
                role: 'Mom of 4',
              },
            ].map((testimonial) => (
              <div
                key={testimonial.name}
                className="p-6 rounded-2xl bg-gray-50 border border-gray-100"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed mb-6">"{testimonial.quote}"</p>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 sm:py-28 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Simple, family-friendly pricing
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Start free and upgrade as your family grows.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                name: 'Free',
                price: '$0',
                period: '/month',
                description: 'Perfect for getting started',
                features: [
                  'Up to 2 children',
                  'Daily checklists',
                  'Star rewards',
                  'Weekday schedules',
                ],
                cta: 'Get Started',
                highlighted: false,
              },
              {
                name: 'Family',
                price: '$5',
                period: '/month',
                description: 'For the whole family',
                features: [
                  'Up to 4 children',
                  'Timeline + Checklists',
                  'Star rewards',
                  'Weekday & Weekend schedules',
                  'Slack notifications',
                  'Completion history',
                ],
                cta: 'Start Free Trial',
                highlighted: true,
              },
              {
                name: 'Extended',
                price: '$9',
                period: '/month',
                description: 'For larger families',
                features: [
                  'Unlimited children',
                  'Everything in Family',
                  'Custom activity types',
                  'Multiple parent accounts',
                  'Priority support',
                ],
                cta: 'Start Free Trial',
                highlighted: false,
              },
            ].map((plan) => (
              <div
                key={plan.name}
                className={`relative p-8 rounded-2xl ${
                  plan.highlighted
                    ? 'bg-white border-2 border-indigo-600 shadow-xl'
                    : 'bg-white border border-gray-200 shadow-sm'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-indigo-600 text-white text-xs font-bold rounded-full uppercase tracking-wide">
                    Most Popular
                  </div>
                )}
                <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-500">{plan.period}</span>
                </div>
                <p className="mt-2 text-sm text-gray-500">{plan.description}</p>
                <ul className="mt-8 space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-sm text-gray-700">
                      <Shield className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  className={`mt-8 w-full py-3 px-4 rounded-xl font-semibold text-sm transition ${
                    plan.highlighted
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-started" className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-purple-700" />
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-20 -right-20 w-60 h-60 bg-white/10 rounded-full" />
              <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-white/10 rounded-full" />
            </div>
            <div className="relative px-8 py-16 sm:px-16 sm:py-20 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white">
                Ready to bring order to your family's day?
              </h2>
              <p className="mt-4 text-lg text-indigo-100 max-w-xl mx-auto">
                Join hundreds of families using LEES to build consistent routines their kids love.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                <button className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white text-indigo-700 font-semibold rounded-xl hover:bg-indigo-50 transition shadow-lg">
                  Get Started for Free
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <p className="mt-4 text-sm text-indigo-200">
                Free forever for up to 2 children. No credit card needed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">📋</span>
                <span className="text-xl font-bold text-gray-900">LEES</span>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">
                Helping families build consistent daily routines for happier, more independent kids.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="#features" className="hover:text-gray-700 transition">Features</a></li>
                <li><a href="#pricing" className="hover:text-gray-700 transition">Pricing</a></li>
                <li><Link to="/timeline" className="hover:text-gray-700 transition">Demo</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="#" className="hover:text-gray-700 transition">About</a></li>
                <li><a href="#" className="hover:text-gray-700 transition">Blog</a></li>
                <li><a href="#" className="hover:text-gray-700 transition">Careers</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="#" className="hover:text-gray-700 transition">Privacy</a></li>
                <li><a href="#" className="hover:text-gray-700 transition">Terms</a></li>
                <li><a href="#" className="hover:text-gray-700 transition">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-gray-100 text-center">
            <p className="text-sm text-gray-400">
              &copy; {new Date().getFullYear()} LEES. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
